export interface UserSettings {
  primaryColor?: string;
  secondaryColor?: string;
  logoUrl?: string;
  language?: string;
}

export interface User {
  id: number;
  login: string;
  firstname: string;
  lastname: string;
  admin: boolean;
  permissions: string[];
  clubNumber?: string | null;
  settings?: UserSettings;
}

export interface AskPasswordForm {
  email: string;
}

export interface ResetPasswordForm {
  password: string;
  passwordConfirmation: string;
  token: string;
}

export interface ClubForm {
  name: string;
  number?: string;
  department: number;
}

export interface Club {
  id: number;
  name: string;
  number?: string;
  department: number;
}

export interface PlayerForm {
  lastname: string;
  firstname: string;
  points?: number;
  clubId: number | null;
  licence: string | null;
  email: string;
  sex: Sex;
  age: AgeCategory;
  language?: string;
}

export interface Player {
  id: number;
  lastname: string;
  firstname: string;
  points?: number;
  club: Club | null;
  licence: string;
  email: string;
  sex: Sex;
  age: AgeCategory;
  language?: string;
}

export interface TournamentBranding {
  logoUrl?: string;
  primaryColor?: string;
}

export interface Tournament {
  id: number;
  name: string;
  location: string;
  nbTables: number;
  maxHeat: number;
  date: Date;
  heats: Heat[];
  posterUrl?: string;
  reglementUrl?: string;
  branding?: TournamentBranding;
}

export interface TournamentForm {
  name: string;
  location: string;
  nbTables: number;
  maxHeat: number;
  date: Date;
}

export interface TournamentConfig {
  id: number;
  date: Date;
  emailEnabled: boolean;
  emailConfig: EmailConfig;
  preRegistrationEnabled: boolean;
  preRegistrationConfig?: formConfig;
}

export type TournamentResourceType = "POSTER" | "RULES" | "LOGO";

export interface formConfig {
  registrationId: string;
  showStats: boolean;
  posterUrl?: string;
  rulesUrl?: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
}

export interface EmailConfig {
  enabled: boolean;
  title: Record<string, string>;
  thanks: Record<string, string>;
  bye: Record<string, string>;
}

export enum HeatStatus {
  PREPARATION = "PREPARATION",
  IN_PROGRESS = "IN_PROGRESS",
  FINISHED = "FINISHED",
}

export interface Heat {
  id: number;
  name: string;
  type: HeatType;
  status: HeatStatus;
  eliminationType: HeatEliminationType;
  pointsLimit: number | null;
  ageCategory: AgeCategory | null;
  sexRestriction: Sex | null;
  playersLimit: number | null;
  nbRegistered: number;
  progress: number;
}

export interface HeatForm {
  name: string;
  type: HeatType;
  eliminationType: HeatEliminationType;
  tournamentId: number;
  pointsLimit: number | null;
  ageCategory: AgeCategory | null;
  sexRestriction: Sex | null;
  playersLimit: number | null;
}

export enum AgeCategory {
  SENIOR = "SENIOR",
  VETERAN = "VETERAN",
  JUNIOR = "JUNIOR",
  BENJAMIN = "BENJAMIN",
  CADET = "CADET",
  MINIME = "MINIME",
  POUSSIN = "POUSSIN",
}

export enum HeatType {
  SIMPLE = "SIMPLE",
  DOUBLE = "DOUBLE",
  HANDICAP = "HANDICAP",
}

export enum Sex {
  MASCULIN = "MASCULIN",
  FEMININ = "FEMININ",
}

export enum HeatEliminationType {
  DIRECTE = "DIRECTE",
  POULE = "POULE",
}

export interface PlayerRegistration {
  id: string;
  player: Player;
  tournament: Tournament;
}

export interface HeatRegistration {
  id: number;
  ranking?: number;
  heatId: number;
  heatName: string;
  players: Player[];
}

export interface HeatRegistrationSummary {
  id: number;
  ranking: number;
  heat: Heat;
}

export interface PlayerTournamentDetail {
  player: Player;
  heatRegistrations: HeatRegistrationSummary[];
}

export interface Table {
  id: number;
  number: number;
}

export interface TableDetail extends Table {
  usable: boolean;
  inUse: boolean;
}

export enum MatchStatus {
  PENDING = "PENDING",
  RUNNING = "RUNNING",
  FINISHED = "FINISHED",
}

export const MatchType = {
  TABLEAU_FINAL: "TABLEAU_FINAL",
  POULE: "POULE",
} as const;

export type MatchType = (typeof MatchType)[keyof typeof MatchType];

export interface MatchDisplay {
  opponents: MatchPlayer[];
}

export interface MatchPlayer {
  name: string;
  club: string;
  ranking: number;
}

export interface Match {
  id: number;
  opponents: HeatRegistration[];
  number: number;
  round: number;
  name: string;
  heatName: string;
  tableNumber: number;
  table: Table;
  type: MatchType;
  status: MatchStatus;
  creationDate: Date;
}

export interface MatchComposition {
  id: string;
  heatRegistration: HeatRegistration;
  index: number;
  qualified: boolean | false;
}

export interface MatchDetail {
  id: number;
  status: MatchStatus | MatchStatus.PENDING;
  round: number;
  number: number;
  type: MatchType;
  heatName: string;
  table: Table;
  dateCreated: Date;
  begin: Date;
  end: Date;
  opponents: MatchComposition[];
  playersFree: boolean;
}

export interface MatchInfo {
  id: string;
  tableNumber: number;
  heatName: string;
}

export interface BusyPlayer {
  player: Player;
  match: MatchInfo;
}

export interface Round {
  number: number;
  matches: MatchDetail[];
}

export interface HeatBracket {
  heat: Heat;
  rounds: Round[];
  roundNames: string[];
}

export interface MatchSlot {
  begin: Date;
  end: Date;
  id: number;
  label: string;
  duration: number;
}

export interface TableMatchSlots {
  table: string;
  totalDayDuration: number;
  matches: MatchSlot[];
}

export interface TournamentTablesSlots {
  tablesSlots: TableMatchSlots[];
  begin: Date;
  end: Date;
}

interface RegistrationTournamentInfo {
  nbRegistered: number;
  registeredByHeats: Record<string, number>;
}

export interface RegistrationForm {
  tournament: Tournament;
  stats: RegistrationTournamentInfo;
  formConfig?: formConfig;
  id: string;
  licence: string;
  lastname: string;
  firstname: string;
  points: number;
  club: string;
  email: string;
  heats: number[];
  message: string;
}

export enum MemberRole {
  OWNER = "OWNER",
  MANAGER = "MANAGER",
}

export interface TournamentMember {
  userId: number;
  login: string;
  firstname: string;
  lastname: string;
  role: MemberRole;
  invitedAt: string;
}

export interface PreRegistration {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  points: number;
  licence: string;
  club: string;
  heats: number[];
  message: string;
  createdDate: Date;
  lastModifiedDate: Date;
}

export type RegistrationRequest = Pick<
  RegistrationForm,
  | "id"
  | "licence"
  | "lastname"
  | "firstname"
  | "points"
  | "club"
  | "email"
  | "heats"
  | "message"
>;

export interface RegistrationConfirmation {
  registrationId: string;
  tournamentId: string;
  tournamentName: string;
  firstname: string;
  lastname: string;
  email: string;
  date: string;
  stats: RegistrationTournamentInfo;
  heatsRegistered: string[];
}

export interface ClubStats {
  label: string;
  value: number;
}

export interface PlayerStats {
  playerName: string;
  nbMatchs: number;
}

export type MetricType = "VALUE" | "PERCENT";

export interface TournamentMetrics {
  label: string;
  type: string;
  value: number;
}

export interface StatsRange {
  min: number;
  max: number;
  count: number;
}

export interface PlayersAgeCategory {
  ageCategory: AgeCategory;
  value: number;
}

export interface HeatRankEntry {
  description: string;
  labelForMatch: string;
}

export interface HeatsRankings {
  heat: Heat;
  ranksForHeat: HeatRankEntry[];
}

export type SurveyQuestionType = "RATING" | "TEXT";

export interface SurveyQuestion {
  id?: number;
  label: string;
  type: SurveyQuestionType;
  order: number;
}

export interface SurveyTemplate {
  enabled: boolean;
  sent?: boolean;
  questions: SurveyQuestion[];
  eligibleCount?: number;
  responseCount?: number;
}

export interface SurveyAnswer {
  questionId: number;
  ratingValue?: number;
  textValue?: string;
}

export interface SurveySubmission {
  answers: SurveyAnswer[];
}

export interface SurveyQuestionResult {
  questionId: number;
  label: string;
  type: SurveyQuestionType;
  order: number;
  averageRating?: number;
  texts?: string[];
}

export interface SurveyResults {
  responseCount: number;
  eligibleCount: number;
  questions: SurveyQuestionResult[];
}

export interface SurveyPublic {
  tournamentName: string;
  tournamentLocation: string;
  tournamentDate: string;
  logoUrl?: string;
  questions: SurveyQuestion[];
  alreadyResponded: boolean;
}
